import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  Trash2, 
  FileText
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DoubleScrollbar from '../../components/UI/DoubleScrollbar';
import toast from 'react-hot-toast';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import { useSettings } from '../../contexts/SettingsContext';
import { formatDateTime } from '../../utils/formatDateTime';
import Button from '../../components/UI/Button';

interface Invoice {
  _id: string;
  invoiceNumber: string;
  type: 'delivery' | 'return' | 'purchase';
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  customerAddress?: string;
  items: Array<{
    product: {
      _id: string;
      name: string;
      sku: string;
    };
    quantity: number;
    unitPrice: number;
    total: number;
  }>;
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
  totalAmount: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  dueDate?: string;
  issueDate: string;
  project?: {
    _id: string;
    name: string;
  };
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// Define the type for an invoice item
interface InvoiceItem {
  product: string;
  quantity: number;
  unitPrice: number;
  total: number;
  name?: string;
  sku?: string;
}

const InvoicesPage: React.FC = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  // Update newInvoice state to use the correct type
  const [newInvoice, setNewInvoice] = useState<{
    invoiceNumber: string;
    customerName: string;
    type: string;
    status: string;
    items: InvoiceItem[];
    subtotal: number;
    taxAmount: number;
    discountAmount: number;
    totalAmount: number;
  }>({
    invoiceNumber: '',
    customerName: '',
    type: 'delivery',
    status: 'draft',
    items: [],
    subtotal: 0,
    taxAmount: 0,
    discountAmount: 0,
    totalAmount: 0,
  });
  // 1. Add state for products and new item
  const [products, setProducts] = useState<any[]>([]);
  const [newItem, setNewItem] = useState({ product: '', quantity: 1, unitPrice: 0 });
  const { settings } = useSettings();
  const invoiceCustomFields = settings?.customFields?.invoices || [];
  const currencySymbol = settings?.invoice?.defaultCurrencySymbol || '৳';

  // In send-email modal/form state initialization:
  const [emailSubject, setEmailSubject] = useState(settings?.email?.templates?.invoice?.subject || 'Your Invoice');
  const [emailBody, setEmailBody] = useState(settings?.email?.templates?.invoice?.body || 'Please find your invoice attached.');

  // 2. Fetch products when modal opens
  useEffect(() => {
    if (showCreateInvoiceModal) {
      apiService.get('/products').then(res => {
        setProducts(res.data.products || []);
      });
    }
  }, [showCreateInvoiceModal]);

  // 3. Add item to invoice
  const handleAddItem = () => {
    if (!newItem.product || newItem.quantity <= 0 || newItem.unitPrice < 0) return;
    const productObj = products.find(p => p._id === newItem.product);
    if (!productObj) return;
    setNewInvoice(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          product: newItem.product,
          quantity: newItem.quantity,
          unitPrice: newItem.unitPrice,
          total: newItem.quantity * newItem.unitPrice,
          name: productObj.name,
          sku: productObj.sku
        }
      ]
    }));
    setNewItem({ product: '', quantity: 1, unitPrice: 0 });
  };

  // 4. Remove item from invoice
  const handleRemoveItem = (idx: number) => {
    setNewInvoice(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx)
    }));
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/invoices');
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        const response = await apiService.delete(`/invoices/${invoiceId}`);
        toast.success('Invoice deleted successfully');
        
        // Immediately clear invoices cache and force refresh
        apiService.invalidateInvoicesCache();
        
        // Force refresh to get updated data with a small delay to ensure cache is cleared
        setTimeout(async () => {
          await fetchInvoices();
        }, 100);
      } catch (error) {
        console.error('Error deleting invoice:', error);
        toast.error('Failed to delete invoice');
      }
    }
  };

  const handleStatusChange = async (invoiceId: string, newStatus: string) => {
    try {
      const response = await apiService.patch(`/invoices/${invoiceId}`, { status: newStatus });
      toast.success('Invoice status updated successfully');
      
      // Immediately clear invoices cache and force refresh
      apiService.invalidateInvoicesCache();
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchInvoices();
      }, 100);
    } catch (error) {
      console.error('Error updating invoice status:', error);
      toast.error('Failed to update invoice status');
    }
  };

  const handleCreateInvoice = async () => {
    try {
      // Omit customerName from payload
      const { customerName, taxAmount, discountAmount, totalAmount, ...rest } = newInvoice;
      // Calculate subtotal
      const subtotal = newInvoice.items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
      // Calculate total
      const tax = newInvoice.taxAmount || 0;
      const discount = newInvoice.discountAmount || 0;
      const total = subtotal + tax - discount;
      const payload = {
        ...rest,
        subtotal,
        tax,
        discount,
        total,
        preparedBy: user?._id,
        customer: { name: customerName },
        items: newInvoice.items.map(item => {
          // Find the product to get the unit
          const productObj = products.find(p => p._id === item.product);
          return {
            product: item.product,
            quantity: item.quantity,
            unit: productObj?.unit || 'pcs',
            unitPrice: item.unitPrice,
            totalPrice: item.quantity * item.unitPrice,
            name: item.name,
            sku: item.sku,
          };
        }),
      };
      const response = await apiService.post('/invoices', payload);
      toast.success('Invoice created successfully');
      
      // Immediately clear invoices cache and force refresh
      apiService.invalidateInvoicesCache();
      
      setShowCreateInvoiceModal(false);
      setNewInvoice({ invoiceNumber: '', customerName: '', type: 'delivery', status: 'draft', items: [], subtotal: 0, taxAmount: 0, discountAmount: 0, totalAmount: 0 });
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchInvoices();
      }, 100);
    } catch (error: any) {
      let errorMsg = 'Failed to create invoice';
      if (error.response?.data?.errors) {
        errorMsg = error.response.data.errors.map((e: any) => e.msg).join(', ');
      } else if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      }
      toast.error(errorMsg);
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !selectedType || invoice.type === selectedType;
    const matchesStatus = !selectedStatus || invoice.status === selectedStatus;
    
    let matchesDate = true;
    if (dateRange.start && dateRange.end) {
      const invoiceDate = new Date(invoice.issueDate);
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      matchesDate = invoiceDate >= startDate && invoiceDate <= endDate;
    }
    
    return matchesSearch && matchesType && matchesStatus && matchesDate;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processing': return 'bg-yellow-100 text-yellow-800';
      case 'sent': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'delivery': return <FileText className="w-4 h-4 text-[var(--color-success)]" />;
      case 'return': return <FileText className="w-4 h-4 text-[var(--color-danger)]" />;
      case 'purchase': return <FileText className="w-4 h-4 text-[var(--color-info)]" />;
      default: return <FileText className="w-4 h-4 text-[var(--color-text-secondary)]" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'delivery': return 'text-[var(--color-success)] bg-[var(--color-success-light)]';
      case 'return': return 'text-[var(--color-danger)] bg-[var(--color-danger-light)]';
      case 'purchase': return 'text-[var(--color-info)] bg-[var(--color-info-light)]';
      default: return 'text-[var(--color-text-secondary)] bg-[var(--color-surface)]';
    }
  };

  const exportInvoices = async () => {
    try {
      await apiService.download('/invoices/export', 'invoices.xlsx');
      toast.success('Invoices exported successfully');
    } catch (error) {
      console.error('Error exporting invoices:', error);
      toast.error('Failed to export invoices');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[var(--color-text)] font-['Times_New_Roman']">Invoices</h1>
          <p className="text-[var(--color-text)] font-['Times_New_Roman']">
            Manage your delivery, return, and purchase invoices
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={exportInvoices}
            className="flex items-center px-4 py-2 bg-[var(--color-surface)] text-[var(--color-text)] rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors font-['Times_New_Roman']"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
          <Button
            variant="primary"
            onClick={() => setShowCreateInvoiceModal(true)}
            icon={<Plus className="w-4 h-4 mr-2" />}
          >
            Create Invoice
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-md p-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-secondary)] w-4 h-4" />
              <input
                type="text"
                placeholder="Search invoices by number, customer, or status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-4 py-2 bg-[var(--color-surface)] text-[var(--color-text)] rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors font-['Times_New_Roman']"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 } as any}
            animate={{ opacity: 1, height: 'auto' } as any}
            exit={{ opacity: 0, height: 0 } as any}
            className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4"
          >
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
            >
              <option value="">All Types</option>
              <option value="delivery">Delivery</option>
              <option value="return">Return</option>
              <option value="purchase">Purchase</option>
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              placeholder="Start Date"
            />

            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              placeholder="End Date"
            />
          </motion.div>
        )}
      </div>

      {/* Invoices List */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-md overflow-hidden">
        <DoubleScrollbar>
          <table className="w-full">
            <thead className="bg-[var(--color-surface)]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Invoice
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[var(--color-background)] divide-y divide-[var(--color-border)]">
              {filteredInvoices.map((invoice) => (
                <motion.tr
                  key={invoice._id}
                  initial={{ opacity: 0 } as any}
                  animate={{ opacity: 1 } as any}
                  className="hover:bg-[var(--color-surface-hover)]"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-[var(--color-text)] font-['Times_New_Roman']">
                        {invoice.invoiceNumber}
                      </div>
                      <div className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                        {invoice.items.length} items
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-[var(--color-text)] font-['Times_New_Roman']">
                        {invoice.customerName || 'N/A'}
                      </div>
                      <div className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                        {invoice.customerEmail || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(invoice.type)}`}>
                      {getTypeIcon(invoice.type)}
                      <span className="ml-1 capitalize font-['Times_New_Roman']">{invoice.type}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-[var(--color-text)] font-['Times_New_Roman']">
                      {currencySymbol}{typeof invoice.totalAmount === 'number' ? invoice.totalAmount.toLocaleString() : ''}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={invoice.status}
                      onChange={(e) => handleStatusChange(invoice._id, e.target.value)}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)} font-['Times_New_Roman']`}
                    >
                      <option value="processing">Processing</option>
                      <option value="sent">Sent</option>
                      <option value="completed">Completed</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    {invoice.issueDate ? formatDateTime(invoice.issueDate, settings) : ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        className="text-[var(--color-info)] hover:text-[var(--color-info-dark)]"
                        title="View Invoice"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        className="text-[var(--color-success)] hover:text-[var(--color-success-dark)]"
                        title="Edit Invoice"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteInvoice(invoice._id)}
                        className="text-[var(--color-danger)] hover:text-[var(--color-danger-dark)]"
                        title="Delete Invoice"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </DoubleScrollbar>

        {/* Empty State */}
        {filteredInvoices.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-[var(--color-text-secondary)] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">
              No invoices found
            </h3>
            <p className="text-[var(--color-text-secondary)] font-['Times_New_Roman']">
              {searchTerm || selectedType || selectedStatus || dateRange.start
                ? 'Try adjusting your search or filters'
                : 'Get started by creating your first invoice'
              }
            </p>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
        Showing {filteredInvoices.length} of {invoices.length} invoices
      </div>

      <Modal isOpen={showCreateInvoiceModal} onClose={() => setShowCreateInvoiceModal(false)} title="Create Invoice" size="xl" className="!max-w-7xl">
        <div className="space-y-4">
          <Input label="Invoice Number" value={newInvoice.invoiceNumber} onChange={e => setNewInvoice({ ...newInvoice, invoiceNumber: e.target.value })} />
          <Input label="Customer Name" value={newInvoice.customerName} onChange={e => setNewInvoice({ ...newInvoice, customerName: e.target.value })} />
          <select value={newInvoice.type} onChange={e => setNewInvoice({ ...newInvoice, type: e.target.value })} className="input">
            <option value="delivery">Delivery</option>
            <option value="return">Return</option>
            <option value="purchase">Purchase</option>
          </select>
          <div className="mb-4">
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1">Add Item</label>
            <div className="flex gap-2 mb-2">
              <select
                className="border rounded px-2 py-1 flex-1"
                value={newItem.product}
                onChange={e => setNewItem({ ...newItem, product: e.target.value })}
              >
                <option value="">Select Product</option>
                {products.map(p => (
                  <option key={p._id} value={p._id}>{p.name} ({p.sku})</option>
                ))}
              </select>
              <input
                type="number"
                min={1}
                className="border rounded px-2 py-1 w-20"
                placeholder="Qty"
                value={newItem.quantity}
                onChange={e => setNewItem({ ...newItem, quantity: Number(e.target.value) })}
              />
              <input
                type="number"
                min={0}
                className="border rounded px-2 py-1 w-24"
                placeholder="Unit Price"
                value={newItem.unitPrice}
                onChange={e => setNewItem({ ...newItem, unitPrice: Number(e.target.value) })}
              />
              <button type="button" className="px-3 py-1 bg-[var(--color-primary)] text-[var(--color-white)] rounded" onClick={handleAddItem}>Add</button>
            </div>
            {/* List of added items */}
            {newInvoice.items.length > 0 && (
              <table className="w-full text-sm mt-2">
                <thead>
                  <tr>
                    <th className="text-left">Product</th>
                    <th>Qty</th>
                    <th>Unit Price</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {newInvoice.items.map((item, idx) => (
                    <tr key={idx}>
                      <td>{products.find(p => p._id === item.product)?.name || item.product}</td>
                      <td className="text-center">{item.quantity}</td>
                      <td className="text-center">{item.unitPrice}</td>
                      <td className="text-center">{item.total}</td>
                      <td><button type="button" className="text-[var(--color-danger)]" onClick={() => handleRemoveItem(idx)}>Remove</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <button className="btn btn-primary w-full" onClick={handleCreateInvoice}>Create Invoice</button>
        </div>
      </Modal>
    </div>
  );
};

export default InvoicesPage; 